/* ──────────────────────────────────────────────────────────────
 *  ExploreControls — Metric filter bar for Explore Designs
 *
 *  Contains: Metric Autocomplete, Min/Max TextFields,
 *  Filter button, Export button.
 * ────────────────────────────────────────────────────────────── */

import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FilterAltIcon  from '@mui/icons-material/FilterAlt';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

interface ExploreControlsProps {
  metricOptions: string[];
  metric: string | null;
  minVal: string;
  maxVal: string;
  onMetricChange: (metric: string | null) => void;
  onMinChange: (val: string) => void;
  onMaxChange: (val: string) => void;
  onFilter: () => void;
  onExport: () => void;
  exportDisabled: boolean;
}

const ExploreControls: React.FC<ExploreControlsProps> = ({
  metricOptions,
  metric,
  minVal,
  maxVal,
  onMetricChange,
  onMinChange,
  onMaxChange,
  onFilter,
  onExport,
  exportDisabled,
}) => {
  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onMinChange(e.target.value),
    [onMinChange],
  );

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onMaxChange(e.target.value),
    [onMaxChange],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        pt: 1.5,
        mb: 1.5,
        width: '100%',
        minWidth: 0,
        overflow: 'visible',
      }}
    >
      <Autocomplete
        size="small"
        options={metricOptions}
        value={metric}
        onChange={(_, val) => onMetricChange(val)}
        sx={{ minWidth: 200, flex: 1 }}
        renderInput={(params) => (
          <TextField {...params} label="Metric" variant="outlined" size="small" />
        )}
      />
      <TextField
        size="small"
        label="Min"
        type="number"
        value={minVal}
        onChange={handleMinChange}
        sx={{ width: 100, flex: 1 }}
      />
      <TextField
        size="small"
        label="Max"
        type="number"
        value={maxVal}
        onChange={handleMaxChange}
        sx={{ width: 100, flex: 1 }}
      />
      <Button
        variant="contained"
        size="medium"
        startIcon={<FilterAltIcon  />}
        onClick={onFilter}
        color="info"
        sx={{ flex: 0.2, minWidth: 40 }}
      >
        Filter
      </Button>
      <Button
        variant="contained"
        size="medium"
        startIcon={<FileDownloadIcon />}
        onClick={onExport}
        disabled={exportDisabled}
        color="info"
        sx={{ flex: 0.2, minWidth: 40, '&.Mui-disabled': { color: 'primary.main' } }}
      >
        Export
      </Button>
    </Box>
  );
};

export default React.memo(ExploreControls);
